import { useMemo } from 'react';

import { GetLevels } from '@/data-explorer-v2/api/queries/get-levels';

import { GetAvailableChartTypes } from '../../api/queries/get-available-chart-types';
import type { Filters } from '../../entities/filters';
import { isAtLowestLevel } from '../../hooks/use-is-at-lowest-level';
import { useDeriveCurrentNode } from '../../utils/derive-node';

import { ChartTypesFilter } from './chart-types-filter';
import { DateRangeFilter } from './date-range-filter';
import { type Domain, DomainToggle } from './domain-toggle';
import { LevelFilters } from './level-filters';
import { NoiseFilter } from './noise-filter';

export type DataExplorerFiltersProps = {
  filters: Filters;
  domain: Domain;
  onChange: (filters: Partial<Filters>) => void;
  onDomainChange: (domain: Domain) => void;
};

export function FilterBar({
  filters,
  domain,
  onChange,
  onDomainChange,
}: DataExplorerFiltersProps) {
  // Derive the current node from filters using utility hook that includes asset ID
  const currentNode = useDeriveCurrentNode(filters, domain);
  const getLevelsData = GetLevels.useQuery({ retry: 3 });

  // Get available chart types for the current node
  const availableChartTypesQuery = GetAvailableChartTypes.useQuery(
    currentNode,
    {
      retry: 3,
    },
  );

  // Determine if we're at the lowest level for noise filter activation
  const isAtLowestLevelValue = isAtLowestLevel(
    filters,
    domain,
    getLevelsData.data,
  );

  // When noise filter should be disabled, ensure it's set to false
  const shouldDisableNoiseFilter = !isAtLowestLevelValue;
  const noiseFilterValue = shouldDisableNoiseFilter
    ? false
    : filters.coreFilters.filterNoise;

  // For PCS domain, map pcsId from URL to the actual level key used by this customer
  // Some customers use battery level names (e.g., "array") for their PCS hierarchy
  const pcsFiltersForDisplay = useMemo(() => {
    if (domain !== 'pcs' || !getLevelsData.data?.pcs) {
      return filters.pcsFilters;
    }

    const pcsLevels = getLevelsData.data.pcs as Record<string, string>;
    const levelKeys = Object.keys(pcsLevels);

    // If there's only one PCS level and we have pcsId in filters, map it to the actual level key
    if (levelKeys.length === 1 && filters.pcsFilters.pcsId) {
      const actualLevelKey = levelKeys[0];
      return {
        ...filters.pcsFilters,
        [`${actualLevelKey}Id`]: filters.pcsFilters.pcsId,
      };
    }

    return filters.pcsFilters;
  }, [domain, getLevelsData.data, filters.pcsFilters]);

  return (
    <div className="filters-wrapper">
      <div className="filters">
        <div className="flex flex-row flex-wrap items-end gap-4">
          <DomainToggle domain={domain} onDomainChange={onDomainChange} />

          <LevelFilters
            domain={domain}
            data={getLevelsData.data}
            isLoading={getLevelsData.isLoading}
            filters={
              domain === 'battery'
                ? filters.batteryFilters
                : pcsFiltersForDisplay
            }
            onChange={(changed) => {
              if (domain === 'battery') {
                onChange({ batteryFilters: changed });
              } else {
                onChange({ pcsFilters: changed });
              }
            }}
          />

          <ChartTypesFilter
            value={filters.coreFilters.chartTypes}
            onChange={(chartTypes) =>
              onChange({
                coreFilters: { ...filters.coreFilters, chartTypes },
              })
            }
            availableChartTypes={availableChartTypesQuery.data}
            isLoading={availableChartTypesQuery.isLoading}
          />

          <DateRangeFilter
            value={{
              from: filters.coreFilters.from,
              to: filters.coreFilters.to,
            }}
            onChange={({ from, to }) =>
              onChange({
                coreFilters: {
                  ...filters.coreFilters,
                  from: from ?? filters.coreFilters.from,
                  to: to ?? filters.coreFilters.to,
                },
              })
            }
          />

          <NoiseFilter
            value={noiseFilterValue}
            disabled={shouldDisableNoiseFilter}
            onChange={(filterNoise) =>
              onChange({
                coreFilters: { ...filters.coreFilters, filterNoise },
              })
            }
          />
        </div>
      </div>
    </div>
  );
}
